import React from 'react';
import { Calendar, Users, FileText, Edit, ArrowLeft, CheckCircle } from 'lucide-react';
import { Project } from '@/services/TrainerServices/GroupProjectManagementService';
import './ViewProject.css';

interface ViewProjectProps {
  project: Project;
  onEdit: () => void;
  onBack: () => void;
}

const ViewProject: React.FC<ViewProjectProps> = ({ project, onEdit, onBack }) => {
  return (
    <div className="admin-group-view-project-container">
      {/* Header */}
      <div className="admin-group-view-project-header">
        <button className="admin-group-view-back-btn" onClick={onBack}>
          <ArrowLeft size={20} />
          Back to Projects
        </button>
        <button className="admin-group-view-edit-btn" onClick={onEdit}>
          <Edit size={20} />
          Edit Project
        </button>
      </div>

      {/* Project Title and Status */}
      <div className="admin-group-view-title-section">
        <div className="admin-group-view-title-wrapper">
          <h1 className="admin-group-view-project-title">{project.name}</h1>
          <span className={`admin-group-view-status-badge admin-group-view-status-${project.status}`}>
            {project.status}
          </span>
        </div>
        <p className="admin-group-view-created-date">
          Created on {project.createdDate ? new Date(project.createdDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : 'N/A'}
        </p>
      </div>

      {/* Main Content - Single Column Layout */}
      <div className="admin-group-view-content">
        {/* Project Information Card */}
        <div className="admin-group-view-card">
          <div className="admin-group-view-card-header">
            <FileText size={24} />
            <h2>Project Information</h2>
          </div>
          <div className="admin-group-view-card-content">
            <div className="admin-group-view-info-row">
              <label>Description</label>
              <p>{project.description || 'No description provided'}</p>
            </div>
            {project.instructions && (
              <div className="admin-group-view-info-row">
                <label>Instructions</label>
                <p>{project.instructions}</p>
              </div>
            )}
            {project.gradingCriteria && (
              <div className="admin-group-view-info-row">
                <label>Grading Criteria</label>
                <p>{project.gradingCriteria}</p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline Card */}
        <div className="admin-group-view-card">
          <div className="admin-group-view-card-header">
            <Calendar size={24} />
            <h2>Timeline</h2>
          </div>
          <div className="admin-group-view-card-content">
            <div className="admin-group-view-timeline-grid">
              <div className="admin-group-view-timeline-item">
                <label>Start Date</label>
                <p>{project.startDate ? new Date(project.startDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'Not specified'}</p>
              </div>
              <div className="admin-group-view-timeline-item">
                <label>Deadline</label>
                <p className="admin-group-view-deadline">
                  {project.deadline ? new Date(project.deadline).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'No deadline set'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Team Settings Card */}
        <div className="admin-group-view-card">
          <div className="admin-group-view-card-header">
            <Users size={24} />
            <h2>Team Settings</h2>
          </div>
          <div className="admin-group-view-card-content">
            <div className="admin-group-view-team-stats">
              <div className="admin-group-view-stat-item">
                <label>Max Teams</label>
                <p className="admin-group-view-stat-value">{project.maxTeams || 0}</p>
              </div>
              <div className="admin-group-view-stat-item">
                <label>Members per Team</label>
                <p className="admin-group-view-stat-value">{project.membersPerTeam || 0}</p>
              </div>
              <div className="admin-group-view-stat-item">
                <label>Current Teams</label>
                <p className="admin-group-view-stat-value">{project.teams || 0}</p>
              </div>
              <div className="admin-group-view-stat-item">
                <label>Total Points</label>
                <p className="admin-group-view-stat-value">{project.totalPoints || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Deliverables Card */}
        <div className="admin-group-view-card">
          <div className="admin-group-view-card-header">
            <CheckCircle size={24} />
            <h2>Deliverables</h2>
          </div>
          <div className="admin-group-view-card-content">
            {project.deliverables && project.deliverables > 0 ? (
              <div className="admin-group-view-deliverables-list">
                <p className="admin-group-view-deliverables-count">
                  {project.deliverables} deliverable{project.deliverables !== 1 ? 's' : ''} required
                </p>
              </div>
            ) : (
              <p className="admin-group-view-empty-text">No deliverables defined</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProject;
